import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Helmet } from "react-helmet-async";
import { PageBody } from "../components/PageBody";
import { PageTitle } from "../components/Headers";
import { Loading } from "../components/Loading";
import { useAuth } from "../context/AuthContext";
import { getDuckDBStatus, getDuckDBRefreshHistory, triggerDuckDBRefresh } from "../api/duckdb";
import {
    ServerStackIcon,
    TableCellsIcon,
    UserGroupIcon,
    DocumentTextIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';

// Helper function to get health status colors
const getHealthColor = (health) => {
    const colors = {
        'healthy': 'bg-green-500',
        'warning': 'bg-yellow-500',
        'degraded': 'bg-orange-500',
        'critical': 'bg-red-500',
        'error': 'bg-red-600'
    };
    return colors[health] || 'bg-gray-500';
};

// Helper function to get operation type badge color
const getOperationTypeColor = (type) => {
    const colors = {
        'scheduled': 'bg-badger-blue',
        'manual': 'bg-badger-purple',
        'auto-init': 'bg-gray-500'
    };
    return colors[type] || 'bg-gray-500';
};

// Helper function to get status badge color
const getStatusColor = (status) => {
    const colors = {
        'success': 'bg-badger-green',
        'failed': 'bg-red-500',
        'partial': 'bg-yellow-500'
    };
    return colors[status] || 'bg-gray-500';
};

// Helper function to format relative time
const getRelativeTime = (timestamp) => {
    if (!timestamp) return 'Never';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
};

// Status Card Component
const StatusCard = ({ title, value, icon: Icon, status, subtitle, lastUpdated }) => (
    <div className="bg-white border border-gray-200 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
            <Icon className="h-8 w-8 text-badger-blue" />
            <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${status === 'exists' || status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`}>
                {status === 'exists' ? 'Active' : status}
            </span>
        </div>
        <h3 className="text-gray-600 text-sm font-medium">{title}</h3>
        <p className="text-3xl font-bold text-gray-900 mt-2">{value?.toLocaleString() || 'N/A'}</p>
        {subtitle && <p className="text-gray-500 text-xs mt-2">{subtitle}</p>}
        {lastUpdated && (
            <p className="text-gray-400 text-xs mt-1">
                Updated: {getRelativeTime(lastUpdated)}
            </p>
        )}
    </div>
);

export const DuckDB = () => {
    const { token, isAuthenticated } = useAuth();
    const queryClient = useQueryClient();
    const [refreshing, setRefreshing] = useState(false);
    const [refreshMessage, setRefreshMessage] = useState(null);

    // Fetch DuckDB status
    const {
        data: statusData,
        isLoading: statusLoading,
        isError: statusError,
        error: statusErrorMsg,
        refetch: refetchStatus
    } = useQuery({
        queryKey: ['duckdb-status'],
        queryFn: getDuckDBStatus,
        refetchInterval: 30000, // Auto-refresh every 30 seconds
        staleTime: 20000
    });

    // Fetch refresh history
    const {
        data: historyData,
        isLoading: historyLoading,
        isError: historyError
    } = useQuery({
        queryKey: ['duckdb-history', 20],
        queryFn: () => getDuckDBRefreshHistory(20),
        refetchInterval: 60000, // Auto-refresh every minute
        staleTime: 45000
    });

    // Manual refresh mutation
    const handleManualRefresh = async () => {
        if (!token) {
            setRefreshMessage({ type: 'error', text: 'You must be logged in to trigger a manual refresh' });
            return;
        }

        setRefreshing(true);
        setRefreshMessage(null);

        try {
            await triggerDuckDBRefresh(token);
            setRefreshMessage({ type: 'success', text: 'DuckDB refresh triggered successfully!' });

            // Invalidate queries to refetch fresh data
            setTimeout(() => {
                queryClient.invalidateQueries('duckdb-status');
                queryClient.invalidateQueries('duckdb-history');
            }, 2000);
        } catch (error) {
            setRefreshMessage({
                type: 'error',
                text: error.response?.data?.error || 'Failed to trigger refresh. Please try again.'
            });
        } finally {
            setRefreshing(false);
            // Clear message after 5 seconds
            setTimeout(() => setRefreshMessage(null), 5000);
        }
    };

    // Loading state
    if (statusLoading && historyLoading) {
        return <Loading loadingText="Loading DuckDB monitoring dashboard..." />;
    }

    // Error state for critical data
    if (statusError) {
        return (
            <PageBody>
                <div className="bg-red-50 border border-red-300 rounded-lg p-6 text-center">
                    <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-red-800 font-semibold mb-2 text-xl">
                        Unable to Load DuckDB Status
                    </h3>
                    <p className="text-red-600 mb-4">
                        {statusErrorMsg?.message || 'An unexpected error occurred while fetching the DuckDB status.'}
                    </p>
                    <button
                        onClick={() => refetchStatus()}
                        className="px-6 py-2 bg-badger-blue text-white rounded-lg hover:bg-blue-600 transition-colors font-semibold"
                    >
                        Retry
                    </button>
                </div>
            </PageBody>
        );
    }

    const status = statusData?.data;
    const history = historyData?.data?.data || [];

    return (
        <PageBody>
            <Helmet>
                <title>DuckDB Monitoring | VizBadger</title>
                <meta name="description" content="Real-time monitoring dashboard for DuckDB analytics database. View health status, table statistics, and refresh operation history."></meta>
            </Helmet>

            <PageTitle title="DuckDB Monitoring" />

            {/* Status Cards Section */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Database Status</h2>
                    {status && (
                        <span className={`mt-2 sm:mt-0 px-4 py-2 rounded-full text-sm font-semibold text-white ${getHealthColor(status.health)} inline-flex items-center`}>
                            {status.health === 'healthy' && <CheckCircleIcon className="h-5 w-5 mr-2" />}
                            {status.health === 'warning' && <ExclamationTriangleIcon className="h-5 w-5 mr-2" />}
                            {(status.health === 'critical' || status.health === 'error') && <XCircleIcon className="h-5 w-5 mr-2" />}
                            {status.health?.toUpperCase()}
                        </span>
                    )}
                </div>

                {statusLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="animate-pulse bg-gray-200 h-40 rounded-lg" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatusCard
                            title="Database"
                            value={status?.database_exists ? "Connected" : "Disconnected"}
                            icon={ServerStackIcon}
                            status={status?.database_exists ? "healthy" : "error"}
                            subtitle={status?.database_exists ? "DuckDB is operational" : "Database not found"}
                        />
                        <StatusCard
                            title="Pool Players"
                            value={status?.tables?.pool_player?.row_count}
                            icon={UserGroupIcon}
                            status={status?.tables?.pool_player?.status || 'unknown'}
                            subtitle={`${status?.tables?.pool_player?.exists ? 'Table exists' : 'Table missing'}`}
                            lastUpdated={status?.tables?.pool_player?.last_updated_at}
                        />
                        <StatusCard
                            title="Pool Matches"
                            value={status?.tables?.pool_match?.row_count}
                            icon={TableCellsIcon}
                            status={status?.tables?.pool_match?.status || 'unknown'}
                            subtitle={`${status?.tables?.pool_match?.exists ? 'Table exists' : 'Table missing'}`}
                            lastUpdated={status?.tables?.pool_match?.last_updated_at}
                        />
                        <StatusCard
                            title="Pageview Events"
                            value={status?.tables?.pageview_events?.row_count}
                            icon={DocumentTextIcon}
                            status={status?.tables?.pageview_events?.status || 'unknown'}
                            subtitle={`${status?.tables?.pageview_events?.exists ? 'Table exists' : 'Table missing'}`}
                            lastUpdated={status?.tables?.pageview_events?.last_updated_at}
                        />
                    </div>
                )}
            </div>

            {/* Last Refresh Card */}
            {status?.last_refresh && (
                <div className="mb-8">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Last Refresh</h2>
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg shadow-md p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Time</p>
                                <p className="text-lg font-semibold text-gray-900">
                                    {getRelativeTime(status.last_refresh.refresh_timestamp)}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {new Date(status.last_refresh.refresh_timestamp).toLocaleString()}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Operation Type</p>
                                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold text-white ${getOperationTypeColor(status.last_refresh.operation_type)}`}>
                                    {status.last_refresh.operation_type}
                                </span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Status</p>
                                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(status.last_refresh.status)}`}>
                                    {status.last_refresh.status}
                                </span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Duration</p>
                                <p className="text-lg font-semibold text-gray-900">
                                    {status.last_refresh.duration_seconds != null ? `${Number(status.last_refresh.duration_seconds).toFixed(2)}s` : 'N/A'}
                                </p>
                            </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-blue-200">
                            <p className="text-sm text-gray-600">
                                <span className="font-semibold">Players:</span> {status.last_refresh.players_synced?.toLocaleString() || 'N/A'} |
                                <span className="font-semibold ml-3">Matches:</span> {status.last_refresh.matches_synced?.toLocaleString() || 'N/A'} |
                                <span className="font-semibold ml-3">Triggered by:</span> {status.last_refresh.triggered_by}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Refresh History Section */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Refresh History</h2>

                    {/* Admin-only Manual Refresh Button */}
                    {isAuthenticated && (
                        <button
                            onClick={handleManualRefresh}
                            disabled={refreshing}
                            className={`mt-4 sm:mt-0 px-4 py-2 rounded-lg font-semibold text-white transition-colors inline-flex items-center ${
                                refreshing
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-badger-purple hover:bg-purple-600'
                            }`}
                        >
                            <ArrowPathIcon className={`h-5 w-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                            {refreshing ? 'Refreshing...' : 'Manual Refresh'}
                        </button>
                    )}
                </div>

                {/* Refresh Message */}
                {refreshMessage && (
                    <div className={`mb-4 p-4 rounded-lg ${
                        refreshMessage.type === 'success'
                            ? 'bg-green-50 border border-green-300 text-green-800'
                            : 'bg-red-50 border border-red-300 text-red-800'
                    }`}>
                        {refreshMessage.text}
                    </div>
                )}

                {/* History Table */}
                {historyLoading ? (
                    <div className="animate-pulse space-y-2">
                        {[1, 2, 3, 4, 5].map(i => (
                            <div key={i} className="bg-gray-200 h-12 rounded" />
                        ))}
                    </div>
                ) : historyError ? (
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 text-center text-yellow-800">
                        Unable to load refresh history. The status information above is still available.
                    </div>
                ) : history.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-300 rounded-lg p-8 text-center text-gray-600">
                        No refresh history available yet.
                    </div>
                ) : (
                    <div className="bg-white border border-gray-200 rounded-lg shadow overflow-hidden">
                        {/* Desktop Table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-100 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Timestamp</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Type</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Duration</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Players</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Matches</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Triggered By</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {history.map((item, index) => (
                                        <tr key={item.refresh_id || index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm">
                                                <span className="font-medium text-gray-900">{getRelativeTime(item.refresh_timestamp)}</span>
                                                <br />
                                                <span className="text-xs text-gray-500">{new Date(item.refresh_timestamp).toLocaleString()}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getOperationTypeColor(item.operation_type)}`}>
                                                    {item.operation_type}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(item.status)}`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">{item.duration_seconds != null ? `${Number(item.duration_seconds).toFixed(2)}s` : 'N/A'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900">{item.players_synced?.toLocaleString() || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900">{item.matches_synced?.toLocaleString() || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{item.triggered_by}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="md:hidden divide-y divide-gray-200">
                            {history.map((item, index) => (
                                <div key={item.refresh_id || index} className="p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <p className="font-medium text-gray-900">{getRelativeTime(item.refresh_timestamp)}</p>
                                            <p className="text-xs text-gray-500">{new Date(item.refresh_timestamp).toLocaleString()}</p>
                                        </div>
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(item.status)}`}>
                                            {item.status}
                                        </span>
                                    </div>
                                    <div className="space-y-1 text-sm">
                                        <p>
                                            <span className="text-gray-600">Type:</span>{' '}
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getOperationTypeColor(item.operation_type)}`}>
                                                {item.operation_type}
                                            </span>
                                        </p>
                                        <p><span className="text-gray-600">Duration:</span> {item.duration_seconds != null ? `${Number(item.duration_seconds).toFixed(2)}s` : 'N/A'}</p>
                                        <p><span className="text-gray-600">Players:</span> {item.players_synced?.toLocaleString() || '-'} | <span className="text-gray-600">Matches:</span> {item.matches_synced?.toLocaleString() || '-'}</p>
                                        <p><span className="text-gray-600">Triggered by:</span> {item.triggered_by}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </PageBody>
    );
};
